import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { evaluateSchoolSubscription } from '../../lib/subscription';
import { savePersistedLogin, isTeacherOrStudentRole } from '../../lib/persistedAuth';
import Logo from '../../assets/Logo.svg';
import '../PasswordVerification/PasswordVerification.css';

const QR_LOGIN_KEY = 'mds_qr_login';

function readQrCredentials() {
  const params = new URLSearchParams(window.location.search);
  const fromUrl = {
    userId: (params.get('id') || '').trim().toLowerCase(),
    password: (params.get('p') || '').trim(),
  };

  if (fromUrl.userId && fromUrl.password) {
    sessionStorage.setItem(QR_LOGIN_KEY, JSON.stringify(fromUrl));
    window.history.replaceState({}, '', '/q');
    return fromUrl;
  }

  const saved = sessionStorage.getItem(QR_LOGIN_KEY);
  if (!saved) return { userId: '', password: '' };

  sessionStorage.removeItem(QR_LOGIN_KEY);
  try {
    const parsed = JSON.parse(saved);
    return {
      userId: String(parsed.userId || '').trim().toLowerCase(),
      password: String(parsed.password || '').trim(),
    };
  } catch {
    return { userId: '', password: '' };
  }
}

const QrLogin = () => {
  const navigate = useNavigate();
  const [error, setError] = useState('');

  useEffect(() => {
    const { userId, password } = readQrCredentials();

    if (!userId || !password) {
      setError('This sign-in link is missing an ID or password.');
      return undefined;
    }

    let cancelled = false;

    const signIn = async () => {
      try {
        const { data: userData, error: userError } = await supabase
          .from('users')
          .select(`
            id,
            user_id,
            first_name,
            last_name,
            full_name,
            email,
            role,
            grade,
            grade_id,
            grade_section_id,
            teachers_grade_ids,
            is_active,
            school_id,
            schools!inner(
              id,
              name,
              school_id,
              contact,
              owner,
              subscription_start_date,
              subscription_end_date,
              subscription_duration_months,
              subscription_paid,
              subscription_warning_days
            )
          `)
          .eq('user_id', userId)
          .single();

        if (userError || !userData) {
          throw new Error('Account not found. Check the ID and password on the card.');
        }

        if (!userData.is_active) {
          throw new Error('Your account is inactive. Please contact your administrator.');
        }

        const subscription = evaluateSchoolSubscription(userData.schools);
        if (!subscription.isAccessible) {
          throw new Error('Account Currently Unavailable');
        }

        const { data: secretRow, error: secretError } = await supabase
          .from('users')
          .select('password')
          .eq('user_id', userId)
          .single();

        if (secretError || String(secretRow?.password || '').trim() !== password) {
          throw new Error('Incorrect password. Use the ID and password printed on the card.');
        }

        if (cancelled) return;

        sessionStorage.setItem('currentUser', JSON.stringify(userData));
        if (isTeacherOrStudentRole(userData.role)) {
          savePersistedLogin(userData);
        }
        navigate('/home', { replace: true });
      } catch (err) {
        if (!cancelled) {
          setError(err.message || 'Could not sign you in.');
        }
      } finally {
        sessionStorage.removeItem(QR_LOGIN_KEY);
      }
    };

    signIn();
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  return (
    <div className="password-page">
      <div className="password-container">
        <div className="password-header">
          <div className="password-logo">
            <img src={Logo} alt="MyDigitalSchools" className="password-logo-image" />
          </div>
          <h1 className="password-title">{error ? 'Sign-in failed' : 'Signing you in'}</h1>
          <p className="password-subtitle">
            {error || 'Opening your MyDigitalSchools account…'}
          </p>
        </div>
        {error && (
          <button type="button" className="password-submit-btn" onClick={() => navigate('/')}>
            Sign in with ID and password
          </button>
        )}
      </div>
    </div>
  );
};

export default QrLogin;
